import { ImmutableUtils } from '@atlassian/mpac-utils';
import { List, Map, Set } from 'immutable';
import flow from 'lodash/flow';
import algoliaFacets from '../../common/config/algolia-facets';
import { getFilters } from '../../common/config/filters';
import { sortOptions } from '../../common/config/sorting';
import { PVA } from '../../common/constants/paymentTypes';
import { BAMBOO, BITBUCKET, COMPASS, CONFLUENCE, CROWD, FECRU, HIPCHAT, JIRA, JIRA_SERVICE_DESK, TRELLO } from '../../common/constants/products';
import SearchParamsUpdate from '../../common/records/SearchParamsUpdate';
import { searchParamsUpdateForAddonKeys } from '../../common/utils/algolia';
export const HOME = 'home';
export const CollectionsTags = {
  SPOTLIGHT: 'marketplaceSpotlight',
  BEST_SELLER: 'marketplaceBestSeller',
  RISING_STAR: 'marketplaceRisingStar'
};
export const CollectionsEnum = {
  FEATURED: 'featured',
  TOP_SELLING: 'topSelling',
  TOP_SELLING_CLOUD: 'topSellingCloud',
  TOP_SELLING_SERVER: 'topSellingServer',
  RECENTLY_ADDED: 'recentlyAdded',
  TRENDING: 'trending',
  NATURAL_WITHOUT_FEATURED: 'naturalWithoutFeatured',
  CLOUD_FORTIFIED: 'cloudFortified',
  SPOTLIGHT: 'spotlight',
  BEST_SELLER: 'bestseller',
  RISING_STAR: 'risingStar'
};
const emptyQuery = Map({
  filters: Map(),
  applyOverrides: SearchParamsUpdate.default_()
});
const baseWithoutAtlassianQuery = emptyQuery.update('applyOverrides', applyOverrides => applyOverrides.compose({
  types: Set([algoliaFacets.get('isAtlassian')]),
  apply: params => params.addFacetRefinement(algoliaFacets.get('isAtlassian').algoliaFacetKey, 'false')
}));
const basePaidViaAtlassianQuery = baseWithoutAtlassianQuery.update('applyOverrides', applyOverrides => applyOverrides.composeApply(p => p.addDisjunctiveFacetRefinement(algoliaFacets.get('marketplaceType').algoliaFacetKey, PVA)));
export const LARGE_COLLECTION_TYPE = 'LARGE_COLLECTION_TYPE';
export const SMALL_COLLECTION_TYPE = 'SMALL_COLLECTION_TYPE';
const resolveProductLabel = _ref => {
  let {
    productKey,
    options
  } = _ref;
  return getFilters({
    featureFlags: options
  }).getIn(['product', 'customFilters', productKey, 'label']);
};
const resolveBaseWithoutAtlassianQueries = _ref2 => {
  let {
    baseFilters,
    baseApplyOverrides
  } = _ref2;
  return resolveQueriesFactory({
    baseQuery: baseWithoutAtlassianQuery,
    baseFilters,
    baseApplyOverrides
  });
};
const resolveQueriesFactory = _ref3 => {
  let {
    baseQuery,
    baseFilters,
    baseApplyOverrides
  } = _ref3;
  baseFilters = baseFilters || Map();
  baseApplyOverrides = baseApplyOverrides || SearchParamsUpdate.default_();
  return function () {
    let {
      filters,
      applyOverrides
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      filters: Map(),
      applyOverrides: SearchParamsUpdate.default_()
    };
    const apo = applyOverrides || SearchParamsUpdate.default_();
    return List([baseQuery.update('filters', fs => fs.merge(baseFilters).merge(filters)).update('applyOverrides', ao => ao.compose(baseApplyOverrides).compose(apo))]);
  };
};
const singleResolveHits = _ref4 => {
  let {
    hits
  } = _ref4;
  return ImmutableUtils.sample(hits.flatten(1), 6);
};
const preserveHitsOrder = addonKeys => hits => hits.sort((a, b) => addonKeys.indexOf(a.get('addonKey')) - addonKeys.indexOf(b.get('addonKey')));
const flattenHits = _ref5 => {
  let {
    hits
  } = _ref5;
  return hits.flatten(1);
};
const customCriteriaCollectionFactory = _ref6 => {
  let {
    key,
    featuredCollectionConfig,
    tileType,
    numDisplayResults,
    marketingLabelFilter
  } = _ref6;
  const flattenRequiredNumberOfHits = _ref7 => {
    let {
      hits
    } = _ref7;
    return ImmutableUtils.sample(hits.flatten(1), numDisplayResults);
  };
  const baseQueries = List([emptyQuery.update('applyOverrides', applyOverrides => applyOverrides.compose(searchParamsUpdateForAddonKeys(featuredCollectionConfig.curatedApps)))]);
  const {
    title,
    tagline,
    curatedApps
  } = featuredCollectionConfig;
  return Map({
    key,
    analyticsTileType: key,
    numResults: 10,
    numDisplayResults,
    title,
    tagline,
    type: tileType,
    usesContentOverride: true,
    resolveQueries: _ref8 => {
      let {
        filters,
        applyOverrides
      } = _ref8;
      return baseQueries.map(bq => bq.update('filters', fs => fs.mergeDeep(filters)).update('applyOverrides', ao => ao.compose(applyOverrides)));
    },
    resolveHits: flow([flattenRequiredNumberOfHits, preserveHitsOrder(curatedApps)]),
    filters: Map({
      marketingLabel: Set([marketingLabelFilter])
    })
  });
};
const spotlightCollectionFactory = featuredCollectionsConfig => {
  const spotlightCollection = featuredCollectionsConfig.find(featuredCollectionConfig => featuredCollectionConfig.key === CollectionsTags.SPOTLIGHT);
  if (spotlightCollection) {
    return customCriteriaCollectionFactory({
      key: CollectionsEnum.SPOTLIGHT,
      featuredCollectionConfig: spotlightCollection,
      tileType: LARGE_COLLECTION_TYPE,
      numDisplayResults: 6,
      marketingLabelFilter: 'Spotlight'
    });
  }
};
const bestSellerCollectionFactory = featuredCollectionsConfig => {
  const bestsellerCollection = featuredCollectionsConfig.find(featuredCollectionConfig => featuredCollectionConfig.key === CollectionsTags.BEST_SELLER);
  if (bestsellerCollection) {
    return customCriteriaCollectionFactory({
      key: CollectionsEnum.BEST_SELLER,
      featuredCollectionConfig: bestsellerCollection,
      tileType: SMALL_COLLECTION_TYPE,
      numDisplayResults: 9,
      marketingLabelFilter: 'Bestseller'
    });
  }
};
const risingStarCollectionFactory = featuredCollectionsConfig => {
  const risingStarCollection = featuredCollectionsConfig.find(featuredCollectionConfig => featuredCollectionConfig.key === CollectionsTags.RISING_STAR);
  if (risingStarCollection) {
    return customCriteriaCollectionFactory({
      key: CollectionsEnum.RISING_STAR,
      featuredCollectionConfig: risingStarCollection,
      tileType: SMALL_COLLECTION_TYPE,
      numDisplayResults: 9,
      marketingLabelFilter: 'Rising Star'
    });
  }
};
const featuredCollectionFactory = _ref9 => {
  let {
    baseQueries,
    numDisplayResults,
    resolveHits,
    title,
    tagline
  } = _ref9;
  return Map({
    key: CollectionsEnum.FEATURED,
    analyticsTileType: 'exploreTile',
    numResults: 10,
    numDisplayResults,
    title,
    tagline,
    type: LARGE_COLLECTION_TYPE,
    hideViewMore: true,
    recordTileImpressions: true,
    usesContentOverride: true,
    resolveQueries: _ref10 => {
      let {
        filters,
        applyOverrides = SearchParamsUpdate.default_()
      } = _ref10;
      return baseQueries.map(bq => bq.update('filters', fs => fs.mergeDeep(filters)).update('applyOverrides', ao => ao.compose(applyOverrides)));
    },
    resolveHits
  });
};
const topSellingCollectionFactory = function topSellingCollectionFactory() {
  let {
    productKey,
    tagline,
    options
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Map({
    key: CollectionsEnum.TOP_SELLING,
    analyticsTileType: 'topSelling',
    numResults: 9,
    title: productKey ? "Top-selling ".concat(resolveProductLabel({
      productKey,
      options
    }), " apps") : 'Top-selling apps',
    tagline,
    type: SMALL_COLLECTION_TYPE,
    resolveQueries: resolveBaseWithoutAtlassianQueries({
      baseFilters: topSellingBaseFilters
    }),
    resolveHits: flattenHits,
    filters: topSellingBaseFilters
  });
};
const topSellingBaseFilters = Map({
  sort: sortOptions.get('topSelling').get('key')
});
const recentlyAddedCollectionFactory = function recentlyAddedCollectionFactory() {
  let {
    productKey,
    tagline,
    options
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Map({
    key: CollectionsEnum.RECENTLY_ADDED,
    analyticsTileType: 'newestTile',
    numResults: 9,
    title: productKey ? "Newest ".concat(resolveProductLabel({
      productKey,
      options
    }), " apps") : 'Newest apps',
    tagline,
    type: SMALL_COLLECTION_TYPE,
    resolveQueries: resolveBaseWithoutAtlassianQueries({
      baseFilters: recentlyAddedBaseFilters
    }),
    resolveHits: flattenHits,
    filters: recentlyAddedBaseFilters
  });
};
const recentlyAddedBaseFilters = Map({
  sort: sortOptions.get('newest').get('key')
});
const trendingCollectionFactory = function trendingCollectionFactory() {
  let {
    productKey,
    tagline,
    options
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Map({
    key: CollectionsEnum.TRENDING,
    analyticsTileType: 'trendingTile',
    numResults: 9,
    title: productKey ? "Trending ".concat(resolveProductLabel({
      productKey,
      options
    }), " apps") : 'Trending apps',
    tagline,
    type: SMALL_COLLECTION_TYPE,
    resolveQueries: resolveBaseWithoutAtlassianQueries({
      baseFilters: trendingBaseFilters
    }),
    resolveHits: flattenHits,
    filters: trendingBaseFilters
  });
};
const trendingBaseFilters = Map({
  sort: sortOptions.get('trending').get('key')
});
const featuredCollectionNumDisplayResults = 6;
export const cloudFortifiedFilter = Map({
  moreFilters: Set(['cloudFortified'])
});
const cloudFortifiedAppsCollectionFactory = () => Map({
  key: CollectionsEnum.CLOUD_FORTIFIED,
  analyticsTileType: 'cloudFortifiedAppTile',
  numResults: 3,
  title: 'Cloud Fortified Apps',
  tagline: "Cloud apps with additional security, reliability, and support to meet your organization's needs.",
  type: LARGE_COLLECTION_TYPE,
  resolveHits: flattenHits,
  hideViewMore: false,
  resolveQueries: resolveQueriesFactory({
    baseQuery: cloudFortifiedAppQuery
  }),
  filters: cloudFortifiedFilter
});
const cloudFortifiedAppQuery = basePaidViaAtlassianQuery.update('applyOverrides', applyOverrides => applyOverrides.composeApply(p => p.addFacetRefinement(algoliaFacets.get('cloudFortified').algoliaFacetKey, 'approved')));
const getHomeCollections = (featuredAddonsConfig, options) => {
  if (featuredAddonsConfig) {
    const featuredCollectionsHomeConfig = featuredAddonsConfig.get(HOME);
    const showCloudFortifiedAppsCollection = options === null || options === void 0 ? void 0 : options.enableCloudFortifiedAppsDiscovery;
    const homeDefaultCollections = [...(showCloudFortifiedAppsCollection ? [cloudFortifiedAppsCollectionFactory()] : [])];
    const customCriteriaCollections = featuredCollectionsHomeConfig && featuredCollectionsHomeConfig.length > 0 ? [spotlightCollectionFactory(featuredCollectionsHomeConfig), bestSellerCollectionFactory(featuredCollectionsHomeConfig), risingStarCollectionFactory(featuredCollectionsHomeConfig)] : [];
    return List([...homeDefaultCollections, ...customCriteriaCollections]);
  }
  return List([]);
};
const getJiraCollections = featuredAddonsConfig => {
  if (featuredAddonsConfig) {
    const featuredCollectionsJiraConfig = featuredAddonsConfig.get(JIRA);
    return featuredCollectionsJiraConfig && featuredCollectionsJiraConfig.length > 0 ? List([spotlightCollectionFactory(featuredCollectionsJiraConfig), bestSellerCollectionFactory(featuredCollectionsJiraConfig), risingStarCollectionFactory(featuredCollectionsJiraConfig)]) : List([]);
  }
  return List([]);
};
const getJiraServiceDeskCollections = featuredAddonsConfig => {
  if (featuredAddonsConfig) {
    const featuredCollectionsJsdConfig = featuredAddonsConfig.get(JIRA_SERVICE_DESK);
    return featuredCollectionsJsdConfig && featuredCollectionsJsdConfig.length > 0 ? List([spotlightCollectionFactory(featuredCollectionsJsdConfig), bestSellerCollectionFactory(featuredCollectionsJsdConfig), risingStarCollectionFactory(featuredCollectionsJsdConfig)]) : List([]);
  }
  return List([]);
};
const getConfluenceCollections = featuredAddonsConfig => {
  if (featuredAddonsConfig) {
    const featuredCollectionsConfluenceConfig = featuredAddonsConfig.get(CONFLUENCE);
    return featuredCollectionsConfluenceConfig && featuredCollectionsConfluenceConfig.length > 0 ? List([spotlightCollectionFactory(featuredCollectionsConfluenceConfig), bestSellerCollectionFactory(featuredCollectionsConfluenceConfig), risingStarCollectionFactory(featuredCollectionsConfluenceConfig)]) : List([]);
  }
  return List([]);
};
const getBitbucketCollections = (featuredAddonsConfig, options) => {
  const commonBitbucketCollections = [topSellingCollectionFactory({
    productKey: BITBUCKET,
    options
  }), recentlyAddedCollectionFactory({
    productKey: BITBUCKET,
    options
  })];
  let featuredBitbucketCollection;
  if (featuredAddonsConfig) {
    featuredBitbucketCollection = featuredCollectionFactory({
      baseQueries: resolveProductQueries({
        productBaseQuery: basePaidViaAtlassianQuery,
        product: BITBUCKET,
        featuredAddonsConfig
      }),
      numDisplayResults: featuredCollectionNumDisplayResults,
      resolveHits: resolveHits(BITBUCKET, featuredAddonsConfig)
    });
    return List([featuredBitbucketCollection, ...commonBitbucketCollections]);
  }
};
const getTrelloCollections = (featuredAddonsConfig, options) => {
  const commonTrelloCollections = [topSellingCollectionFactory({
    productKey: TRELLO,
    options
  }), recentlyAddedCollectionFactory({
    productKey: TRELLO,
    options
  }), trendingCollectionFactory({
    productKey: TRELLO,
    options
  })];
  const featuredTrelloCollection = featuredCollectionFactory({
    baseQueries: resolveProductQueries({
      productBaseQuery: basePaidViaAtlassianQuery,
      product: TRELLO,
      featuredAddonsConfig
    }),
    numDisplayResults: featuredCollectionNumDisplayResults,
    resolveHits: resolveHits(TRELLO, featuredAddonsConfig)
  });
  return List([featuredTrelloCollection, ...commonTrelloCollections]);
};
const getHipchatCollections = (featuredAddonsConfig, options) => {
  const commonHipchatCollections = [recentlyAddedCollectionFactory({
    productKey: HIPCHAT,
    options
  }), trendingCollectionFactory({
    productKey: HIPCHAT,
    options
  })];
  const featuredHipchatCollection = featuredCollectionFactory({
    baseQueries: resolveProductQueries({
      productBaseQuery: baseWithoutAtlassianQuery.update('filters', filters => filters.set('marketingLabel', Set(['hipchat-default']))),
      product: HIPCHAT,
      featuredAddonsConfig
    }),
    numDisplayResults: featuredCollectionNumDisplayResults,
    resolveHits: resolveHits(HIPCHAT, featuredAddonsConfig)
  });
  return List([featuredHipchatCollection, ...commonHipchatCollections]);
};
const getFecruCollections = (featuredAddonsConfig, options) => {
  const commonFecruCollections = [topSellingCollectionFactory({
    productKey: FECRU,
    options
  }), recentlyAddedCollectionFactory({
    productKey: FECRU,
    options
  }), trendingCollectionFactory({
    productKey: FECRU,
    options
  })];
  const featuredFecruCollection = featuredCollectionFactory({
    baseQueries: resolveProductQueries({
      productBaseQuery: basePaidViaAtlassianQuery,
      product: FECRU,
      featuredAddonsConfig
    }),
    numDisplayResults: featuredCollectionNumDisplayResults,
    resolveHits: resolveHits(FECRU, featuredAddonsConfig)
  });
  return List([featuredFecruCollection, ...commonFecruCollections]);
};
const getBambooCollections = (featuredAddonsConfig, options) => {
  const commonBambooCollections = [topSellingCollectionFactory({
    productKey: BAMBOO,
    options
  }), recentlyAddedCollectionFactory({
    productKey: BAMBOO,
    options
  }), trendingCollectionFactory({
    productKey: BAMBOO,
    options
  })];
  const featuredBambooCollection = featuredCollectionFactory({
    baseQueries: resolveProductQueries({
      productBaseQuery: basePaidViaAtlassianQuery,
      product: BAMBOO,
      featuredAddonsConfig
    }),
    numDisplayResults: featuredCollectionNumDisplayResults,
    resolveHits: resolveHits(BAMBOO, featuredAddonsConfig)
  });
  return List([featuredBambooCollection, ...commonBambooCollections]);
};
const getCompassCollections = featuredAddonsConfig => {
  const featuredCompassCollection = featuredCollectionFactory({
    baseQueries: resolveProductQueries({
      productBaseQuery: baseWithoutAtlassianQuery,
      product: COMPASS,
      featuredAddonsConfig
    }),
    numDisplayResults: featuredCollectionNumDisplayResults,
    resolveHits: resolveHits(COMPASS, featuredAddonsConfig)
  });
  return List([featuredCompassCollection]);
};
const getCrowdCollections = (featuredAddonsConfig, options) => {
  const commonCrowdCollections = [recentlyAddedCollectionFactory({
    productKey: CROWD,
    options
  }), trendingCollectionFactory({
    productKey: CROWD,
    options
  })];
  const featuredCrowdCollection = featuredCollectionFactory({
    baseQueries: resolveProductQueries({
      productBaseQuery: baseWithoutAtlassianQuery,
      product: CROWD,
      featuredAddonsConfig
    }),
    numDisplayResults: featuredCollectionNumDisplayResults,
    resolveHits: resolveHits(CROWD, featuredAddonsConfig)
  });
  return List([featuredCrowdCollection, ...commonCrowdCollections]);
};
const resolveHits = (product, featuredAddonsConfig) => {
  const featuredAddons = featuredAddonsConfig.get(product);
  return featuredAddons !== null && featuredAddons !== void 0 && featuredAddons.curatedApps ? flow([singleResolveHits, preserveHitsOrder(featuredAddons.curatedApps)]) : singleResolveHits;
};
const resolveProductQueries = _ref11 => {
  let {
    productBaseQuery,
    product,
    featuredAddonsConfig
  } = _ref11;
  const featuredAddons = featuredAddonsConfig.get(product);
  return List([featuredAddons !== null && featuredAddons !== void 0 && featuredAddons.curatedApps ? emptyQuery.update('applyOverrides', applyOverrides => applyOverrides.compose(searchParamsUpdateForAddonKeys(featuredAddons.curatedApps))) : productBaseQuery.update('filters', filters => filters.set('product', product))]);
};
export default (_ref12 => {
  let {
    featuredAddonsConfig,
    options
  } = _ref12;
  const homeCollections = getHomeCollections(featuredAddonsConfig, options);
  const jiraCollections = getJiraCollections(featuredAddonsConfig);
  const jiraServiceDeskCollections = getJiraServiceDeskCollections(featuredAddonsConfig);
  const confluenceCollections = getConfluenceCollections(featuredAddonsConfig);
  const bitbucketCollections = getBitbucketCollections(featuredAddonsConfig, options);
  const trelloCollections = getTrelloCollections(featuredAddonsConfig, options);
  const hipchatCollections = getHipchatCollections(featuredAddonsConfig, options);
  const fecruCollections = getFecruCollections(featuredAddonsConfig, options);
  const bambooCollections = getBambooCollections(featuredAddonsConfig, options);
  const crowdCollections = getCrowdCollections(featuredAddonsConfig, options);
  const compassCollections = getCompassCollections(featuredAddonsConfig);
  return Map({
    [HOME]: homeCollections,
    [JIRA]: jiraCollections,
    [COMPASS]: compassCollections,
    [JIRA_SERVICE_DESK]: jiraServiceDeskCollections,
    [CONFLUENCE]: confluenceCollections,
    [BITBUCKET]: bitbucketCollections,
    [TRELLO]: trelloCollections,
    [HIPCHAT]: hipchatCollections,
    [FECRU]: fecruCollections,
    [BAMBOO]: bambooCollections,
    [CROWD]: crowdCollections
  });
});